import React from 'react';
import PropTypes from 'prop-types';

import { Typography } from '@mui/material';

import AsyncFormDialog from '../../lib/AsyncFormDialog';

/**
 * Generic confirm-action dialog used across admin destructive operations.
 *
 * A thin wrapper around AsyncFormDialog that pre-fills sensible defaults
 * for confirmation flows (red contained primary button, "Confirm" /
 * "Cancel" labels). The async/error/lock plumbing lives in
 * AsyncFormDialog.
 *
 * Props:
 * - open: bool
 * - title: string
 * - description: string | node
 * - confirmLabel: string (default 'Confirm')
 * - confirmColor: MUI button color (default 'error')
 * - confirmText?: string — when set, the user must type this exact phrase
 *   into a TextField before the confirm button enables. Use for high-
 *   blast-radius destructive actions (e.g. broker deletes), passing the
 *   dataset name as the required phrase. (DES2)
 * - onConfirm(): async — throws on failure (rendered as inline error)
 * - onClose(): void
 */
export default function ConfirmActionDialog({
    open, title, description, confirmLabel = 'Confirm',
    confirmColor = 'error', confirmText, onConfirm, onClose
}) {
    return (
        <AsyncFormDialog
            open={open}
            onClose={onClose}
            title={title}
            submitLabel={confirmLabel}
            submitColor={confirmColor}
            confirmText={confirmText}
            onSubmit={onConfirm}
            >
            <Typography variant="body1">{description}</Typography>
        </AsyncFormDialog>
    );
}

ConfirmActionDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    confirmLabel: PropTypes.string,
    confirmColor: PropTypes.string,
    confirmText: PropTypes.string,
    onConfirm: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};
